import { SidebarProvider } from '@exam-center/app-frame';
import { theme } from '@exam-center/components';
import { ConfigProvider } from '@exam-center/configuration';
import { Routes } from '@generouted/react-router';
import * as Sentry from '@sentry/react';
import { ApolloProvider, createApolloClient } from '@shared/apollo';
import { AuthenticationProvider, getExternalForceLogout } from '@shared/auth';
import { CssBaseline, ThemeProvider } from '@shared/components';
import { LoggerProvider } from '@shared/logger';
import { PermissionProvider } from '@shared/permissions';
import { SnackbarProvider } from '@shared/snackbar';
import { NuqsAdapter } from 'nuqs/adapters/react-router';
import { StrictMode } from 'react';
import * as ReactDOM from 'react-dom/client';

import { configuration } from './configuration';

const dsn = import.meta.env.VITE_SENTRY_DSN;

if (dsn && !import.meta.env.DEV) {
  Sentry.init({
    dsn,
    sendDefaultPii: true,
    environment: import.meta.env.MODE,
  });
}

const { logLevel, graphql } = configuration;
const defaultFetchPolicy = graphql.defaultFetchPolicy ?? 'cache-first';

function App() {
  const client = createApolloClient(
    graphql.apiUrl,
    {
      query: { fetchPolicy: defaultFetchPolicy },
      watchQuery: { fetchPolicy: defaultFetchPolicy },
    },
    {
      onUnauthorized: () => getExternalForceLogout()?.(),
    },
  );

  return (
    <ConfigProvider config={configuration}>
      <ApolloProvider client={client}>
        <LoggerProvider config={{ level: logLevel, enableConsole: true }}>
          <AuthenticationProvider>
            <PermissionProvider>
              <NuqsAdapter>
                <ThemeProvider theme={theme}>
                  <SnackbarProvider
                    options={{
                      snackbarVariant: {
                        pending: 'muted',
                      },
                    }}
                  >
                    <SidebarProvider>
                      <CssBaseline />
                      <Routes />
                    </SidebarProvider>
                  </SnackbarProvider>
                </ThemeProvider>
              </NuqsAdapter>
            </PermissionProvider>
          </AuthenticationProvider>
        </LoggerProvider>
      </ApolloProvider>
    </ConfigProvider>
  );
}

const root = ReactDOM.createRoot(document.getElementById('root') as HTMLElement);

root.render(
  <StrictMode>
    <App />
  </StrictMode>,
);
